import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    Download,
    AlertTriangle,
    ExternalLink,
    ShieldCheck,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';
import FilingSubmissionPanel from '../components/FilingSubmissionPanel';
import SkvStyles from '../components/SkvStyles';

// Skarpa filöverföringstjänsten (kräver e-legitimation). Verifierad mot
// skatteverket.se/filoverforing 2026-08-13 — länken "Inloggning" där.
// OBS: www1.skatteverket.se/fv/fv_web är TESTTJÄNSTEN ("Testa filer") —
// gamla länken hit öppnade dessutom en felsida (systemval.do utan session).
const FILOVERFORING_URL = 'https://sso.skatteverket.se/fv_ext/fv_web/login.do';

/** INK2S rows we emit, in blankettordning (codes per Skatteverkets 2025P4).
 *  [kod, punkt, benämning, sida] — sida '+' ökar det skattemässiga
 *  resultatet, '−' minskar det. */
const INK2S_ROWS = [
    ['7650', '4.1', 'Årets resultat, vinst', '+'],
    ['7750', '4.2', 'Årets resultat, förlust', '−'],
    ['7651', '4.3a', 'Skatt på årets resultat', '+'],
    ['7653', '4.3c', 'Andra bokförda kostnader som inte ska dras av', '+'],
    ['7754', '4.5c', 'Andra bokförda intäkter som inte ska tas upp', '−'],
    ['7654', '4.6a', 'Schablonintäkt på periodiseringsfonder', '+'],
    ['7673', '4.6d', 'Uppräknat belopp vid återföring av periodiseringsfond', '+'],
    ['7763', '4.14a', 'Outnyttjat underskott från föregående år', '−'],
    ['7670', '4.15', 'Överskott (förs till INK2 p. 1.1)', '='],
    ['7770', '4.16', 'Underskott (förs till INK2 p. 1.2)', '='],
];

const INK2S_OVRIGA = [
    ['8026', '4.20', 'Lån från aktieägare (fysisk person) vid räkenskapsårets utgång'],
    ['8022', '4.21', 'Pensionskostnader (som ingår i p. 3.8)'],
];

/** INK2 första sidan — underlag för inkomstskatt m.m. */
const INK2_ROWS = [
    ['7104', '1.1', 'Överskott av näringsverksamhet'],
    ['7114', '1.2', 'Underskott av näringsverksamhet'],
    ['7132', '1.4', 'Underlag för särskild löneskatt på pensionskostnader'],
    ['7133', '1.5', 'Negativt underlag för särskild löneskatt på pensionskostnader'],
];

/**
 * Step 8 — Deklarera (INK2). Builds INK2 + INK2R + INK2S from the låsta
 * året, runs the SRU coverage gate (unmapped accounts get a guided
 * "sätt fältkod" table), asks the two INK2S questions (biträde/revision),
 * and generates info.sru + blanketter.sru for upload in Skatteverkets
 * filöverföringstjänst. LedgerLoop generates and guides — the user files.
 */
const DeklarationStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [preview, setPreview] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const [bitratt, setBitratt] = useState(null);   // "har uppdragstagare biträtt?"
    const [revision, setRevision] = useState(null); // "har ÅR varit föremål för revision?"
    // Skattekonton utanför 8910-8919 (D3 2026-08-11): 8930/8980 kräver besked
    // innan filerna kan skapas; 8920 hanteras automatiskt (alltid 4.3a).
    const [skatt8930, setSkatt8930] = useState(null); // 'ej_skattepliktig' | 'skattepliktig'
    const [skatt8980, setSkatt8980] = useState(null); // 'svensk' | 'utlandsk'
    // C4 2026-08-11: 7668 fondandelar (kan inte läsas ur bokföringen) och
    // 4.20-justering (lånet kan avse närstående som inte är aktieägare).
    const [fondandelar, setFondandelar] = useState('');   // draft (kr)
    const [lanOverride, setLanOverride] = useState('');   // draft (kr)
    const [fondandelarApplied, setFondandelarApplied] = useState('');
    const [lanOverrideApplied, setLanOverrideApplied] = useState('');

    // Belopps-normalisering (granskning 3 fynd 5): svensk formatering
    // accepteras; ogiltigt värde appliceras ALDRIG tyst — det sägs ifrån.
    const applyAmount = (raw, setApplied, label) => {
        const trimmed = String(raw).trim();
        if (trimmed === '') { setApplied(''); return; }
        const normalized = trimmed.replace(/[\s  ]/g, '').replace(',', '.');
        const n = Number(normalized);
        if (!Number.isFinite(n) || n < 0) {
            toast.error(`Ogiltigt belopp för ${label}: "${raw}" — ange ett icke-negativt tal i kronor.`);
            return;
        }
        setApplied(String(n));
    };

    const [overrideDrafts, setOverrideDrafts] = useState({}); // accountNumber -> code
    const [savingOverride, setSavingOverride] = useState(null);

    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(false);
    const [generationEvidence, setGenerationEvidence] = useState(null);
    const [filingConfirmed, setFilingConfirmed] = useState(false);
    const [generateError, setGenerateError] = useState(null);

    const fetchPreview = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getInk2Preview(companyId, processId, {
                bitratt, revision, restitueradSkatt8930: skatt8930, ovrigaSkatter8980: skatt8980,
                lanFranAktieagare: lanOverrideApplied, schablonintaktFondandelar: fondandelarApplied,
            });
            setPreview(resp?.data || null);
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte ta fram deklarationsunderlaget. Kontrollera att skatteberäkningen är bokförd (steg 4).');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId, bitratt, revision, skatt8930, skatt8980,
        lanOverrideApplied, fondandelarApplied]);

    useEffect(() => { fetchPreview({ silent: true }); }, [fetchPreview]);

    const coverage = preview?.coverage;
    const consistency = preview?.consistency;
    const taxReview = preview?.taxAccountReview;
    const needs8930 = Boolean(taxReview && taxReview.restitueradSkatt8930 !== 0);
    const needs8980 = Boolean(taxReview && taxReview.ovrigaSkatter8980 !== 0);
    const questionsAnswered = bitratt != null && revision != null
        && (!needs8930 || skatt8930 != null) && (!needs8980 || skatt8980 != null);
    const canGenerate = Boolean(
        coverage?.complete && consistency?.ok && questionsAnswered && !generating,
    );

    const ink2s = preview?.blocks?.INK2S || {};
    const ink2 = preview?.blocks?.INK2 || {};

    // INK2R grupperat per blankettsektion (Tillgångar, Eget kapital och
    // skulder, Resultaträkning …) i display-ordning.
    const ink2rSections = useMemo(() => {
        const groups = [];
        (preview?.ink2rLines || []).forEach((line) => {
            const last = groups[groups.length - 1];
            if (last && last.section === line.section) last.lines.push(line);
            else groups.push({ section: line.section, lines: [line] });
        });
        return groups;
    }, [preview]);

    const fmtYmd = (v) => (v ? String(v).replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3') : '');

    // ---- Actions ----------------------------------------------------------

    const handleSaveOverride = async (accountNumber) => {
        const code = (overrideDrafts[accountNumber] || '').trim();
        if (!code) return;
        setSavingOverride(accountNumber);
        try {
            await YearEndService.setSruOverride(companyId, accountNumber, code);
            toast.success(`Konto ${accountNumber} kopplades till fältkod ${code}.`);
            setOverrideDrafts((d) => ({ ...d, [accountNumber]: '' }));
            await fetchPreview({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara fältkoden.');
        } finally {
            setSavingOverride(null);
        }
    };

    const downloadBase64 = (filename, contentBase64) => {
        const bytes = Uint8Array.from(atob(contentBase64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setGenerateError(null);
        try {
            const resp = await YearEndService.generateInk2Files(companyId, processId, {
                bitratt, revision, restitueradSkatt8930: skatt8930, ovrigaSkatter8980: skatt8980,
                lanFranAktieagare: lanOverrideApplied, schablonintaktFondandelar: fondandelarApplied,
            });
            (resp?.data?.files || []).forEach((f) => downloadBase64(f.filename, f.contentBase64));
            setGenerated(true);
            setGenerationEvidence(resp?.data?.evidence || null);
            setFilingConfirmed(false);
            toast.success('SRU-filerna är skapade och nedladdade.');
        } catch (err) {
            setGenerateError(err.response?.data?.message || 'Kunde inte skapa SRU-filerna.');
        } finally {
            setGenerating(false);
        }
    };

    // ---- Render -----------------------------------------------------------

    // Ja/Nej som blankettkryss — samma segmentstil som intro-kortets SegToggle.
    const renderQuestion = (value, setValue, name) => (
        <span className="skv-seg" role="group" aria-label={name}>
            <button
                type="button"
                className={value === true ? 'active' : ''}
                aria-pressed={value === true}
                onClick={() => setValue(true)}
            >
                Ja
            </button>
            <button
                type="button"
                className={value === false ? 'active' : ''}
                aria-pressed={value === false}
                onClick={() => setValue(false)}
            >
                Nej
            </button>
        </span>
    );

    return (
        <>
            <SkvStyles />

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        Deklarera (INK2)
                        {preview?.fiscalYear?.year ? ` — räkenskapsåret ${preview.fiscalYear.year}` : ''}
                    </h4>
                    <p className="section-subtitle">
                        Underlaget hämtas direkt från det låsta bokslutet: räkenskapsschemat (INK2R)
                        från kontosaldona och de skattemässiga justeringarna (INK2S) från
                        skatteberäkningen i steg 4. Du laddar ner två filer och lämnar in dem i
                        Skatteverkets filöverföringstjänst — LedgerLoop skickar aldrig in något åt dig.
                    </p>
                    {/* SFL 32:2 (elektroniskt) — härledd frist; helgdag kan
                        flytta fram den, därför "tidigast senast". */}
                    {preview?.deklarationsdeadline?.date && (
                        <p className="section-subtitle" style={{ marginTop: 4 }}>
                            <strong>Deklarationsfrist:</strong> senast{' '}
                            {preview.deklarationsdeadline.date} vid elektronisk inlämning
                            (SFL 32 kap. 2 §). Infaller dagen på helg flyttas fristen fram —
                            se Skatteverkets publicerade datum.
                        </p>
                    )}
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchPreview()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'ye-spin' : undefined} />
                        Uppdatera
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
            {loadError && (
                <div className="alert alert-warning d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{loadError}</span>
                </div>
            )}

            {preview && (
                <>
                    {/* Coverage gate */}
                    {!coverage?.complete && (
                        <div className="ye-ps-section">
                            <h5 className="ye-section-heading">
                                <AlertTriangle size={16} style={{ color: 'var(--bs-warning)', marginRight: 6 }} />
                                {coverage.unmapped.length} konton saknar SRU-fältkod
                            </h5>
                            <p className="text-muted" style={{ maxWidth: '68ch' }}>
                                Dessa konton har saldon men ingår inte i BAS standardkoppling —
                                vanligtvis egna konton eller konton från en SIE-import. Ange vilken
                                fältkod i räkenskapsschemat respektive konto hör till (koden
                                kontrolleras mot Skatteverkets fältnamnstabell). Titta på ett
                                liknande BAS-konto om du är osäker.
                            </p>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th>Konto</th>
                                            <th>Namn</th>
                                            <th className="text-end">Belopp</th>
                                            <th style={{ width: 220 }}>Fältkod</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {coverage.unmapped.map((row) => (
                                            <tr key={row.accountNumber}>
                                                <td>{row.accountNumber}</td>
                                                <td>{row.accountName}</td>
                                                <td className="text-end">{formatSek(row.amount)}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <input
                                                            className="form-control form-control-sm"
                                                            placeholder="t.ex. 7513"
                                                            value={overrideDrafts[row.accountNumber] || ''}
                                                            onChange={(e) => setOverrideDrafts((d) => ({
                                                                ...d, [row.accountNumber]: e.target.value,
                                                            }))}
                                                        />
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            disabled={savingOverride === row.accountNumber
                                                                || !(overrideDrafts[row.accountNumber] || '').trim()}
                                                            onClick={() => handleSaveOverride(row.accountNumber)}
                                                        >
                                                            {savingOverride === row.accountNumber ? 'Sparar…' : 'Spara'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Consistency */}
                    {coverage?.complete && !consistency?.ok && (
                        <div className="alert alert-danger d-flex align-items-center gap-2">
                            <AlertTriangle size={18} />
                            <span>
                                Årets resultat i räkenskapsschemat stämmer inte med skatteberäkningens —
                                kontrollera att alla bokslutsverifikat är bokförda innan du fortsätter.
                            </span>
                        </div>
                    )}

                    {/* Blankett 1: INK2 första sidan */}
                    <div className="ye-ps-section">
                        <div className="skv-sheet">
                            <div className="skv-sheet-head">
                                <div>
                                    <span className="skv-form-chip">INK2</span>
                                    <span className="skv-form-title">Inkomstdeklaration 2 — Aktiebolag</span>
                                </div>
                                <div className="skv-period">
                                    Räkenskapsår {fmtYmd(ink2['7011'])} – {fmtYmd(ink2['7012'])}
                                </div>
                            </div>
                            <div className="skv-section-title">1. Underlag för inkomstskatt m.m.</div>
                            {INK2_ROWS.map(([kod, punkt, label]) => (
                                <div className="skv-row" key={kod}>
                                    <span className="skv-punkt">{punkt}</span>
                                    <span className="skv-label">{label}</span>
                                    <span className={`skv-box${ink2[kod] == null ? ' empty' : ''}`}>
                                        <span className="skv-kod">{kod}</span>
                                        <span className="skv-belopp">{ink2[kod] != null ? Number(ink2[kod]).toLocaleString('sv-SE') : ''}</span>
                                    </span>
                                </div>
                            ))}
                            <p className="skv-note">
                                Beloppen förs över från INK2S (4.15/4.16) och skatteberäkningen —
                                allt i hela kronor, precis som i filen som lämnas in.
                            </p>
                        </div>
                    </div>

                    {/* Blankett 2: INK2R räkenskapsschema */}
                    <div className="ye-ps-section">
                        <div className="skv-sheet">
                            <div className="skv-sheet-head">
                                <div>
                                    <span className="skv-form-chip">INK2R</span>
                                    <span className="skv-form-title">Räkenskapsschema</span>
                                </div>
                                <div className="skv-period">
                                    Balans- och resultaträkningen i Skatteverkets radformat
                                </div>
                            </div>
                            {ink2rSections.map((group) => (
                                <React.Fragment key={group.section}>
                                    <div className="skv-section-title">{group.section}</div>
                                    {group.lines.map((line) => (
                                        <div className="skv-row" key={`${line.inkRow}-${line.faltKod}`}>
                                            <span className="skv-punkt">{line.inkRow}</span>
                                            <span className="skv-label">{line.benamning}</span>
                                            <span className="skv-box">
                                                <span className="skv-kod">{line.faltKod}</span>
                                                <span className="skv-belopp">{Number(line.amount).toLocaleString('sv-SE')}</span>
                                            </span>
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}
                            {(preview.flips || []).map((flip) => (
                                <p className="skv-note" key={`${flip.fromInkRow}-${flip.toInkRow}`}>
                                    Saldot på {flip.fromInkRow} {flip.fromBenamning} låg på motsatt
                                    sida ({flip.amount.toLocaleString('sv-SE')} kr) och redovisas
                                    därför som {flip.toInkRow} {flip.toBenamning} — en balanspost
                                    skrivs aldrig negativ i räkenskapsschemat.
                                </p>
                            ))}
                            <p className="skv-note">
                                Rader utan belopp lämnas tomma i filen och visas inte här.
                                Kopplingstabell: BAS {preview.mappingVersion?.slice?.(0, 10) || preview.mappingVersion}.
                            </p>
                        </div>
                    </div>

                    {/* Blankett 3: INK2S skattemässiga justeringar + frågorna */}
                    <div className="ye-ps-section">
                        <div className="skv-sheet">
                            <div className="skv-sheet-head">
                                <div>
                                    <span className="skv-form-chip">INK2S</span>
                                    <span className="skv-form-title">Skattemässiga justeringar</span>
                                </div>
                                <div className="skv-period">
                                    Bryggan från bokfört resultat till beskattningsbar inkomst
                                </div>
                            </div>
                            <div className="skv-section-title">4. Beräkning av det skattemässiga resultatet</div>
                            {INK2S_ROWS.filter(([kod]) => ink2s[kod] != null).map(([kod, punkt, label, sida]) => (
                                <div className={`skv-row${sida === '=' ? ' skv-row-sum' : ''}`} key={kod}>
                                    <span className="skv-punkt">{punkt}</span>
                                    <span className="skv-label">
                                        {label}
                                        {sida !== '=' && <span className="skv-sida">{sida === '+' ? ' (ökar)' : ' (minskar)'}</span>}
                                    </span>
                                    <span className="skv-box">
                                        <span className="skv-kod">{kod}</span>
                                        <span className="skv-belopp">{Number(ink2s[kod]).toLocaleString('sv-SE')}</span>
                                    </span>
                                </div>
                            ))}
                            {INK2S_OVRIGA.some(([kod]) => ink2s[kod] != null) && (
                                <>
                                    <div className="skv-section-title">Övriga uppgifter</div>
                                    {INK2S_OVRIGA.filter(([kod]) => ink2s[kod] != null).map(([kod, punkt, label]) => (
                                        <div className="skv-row" key={kod}>
                                            <span className="skv-punkt">{punkt}</span>
                                            <span className="skv-label">{label}</span>
                                            <span className="skv-box">
                                                <span className="skv-kod">{kod}</span>
                                                <span className="skv-belopp">{Number(ink2s[kod]).toLocaleString('sv-SE')}</span>
                                            </span>
                                        </div>
                                    ))}
                                </>
                            )}
                            <div className="skv-section-title">Övriga deklarationsuppgifter</div>
                            <div className="skv-question">
                                <span className="skv-label">
                                    Schablonintäkt på fondandelar (4.6 b) — ägde företaget
                                    fondandelar vid kalenderårets ingång anges den beräknade
                                    schablonintäkten (0,4 % av värdet 1 januari) här. Kan inte
                                    läsas ur bokföringen.
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className="skv-inline-input"
                                    placeholder="0 kr"
                                    aria-label="Schablonintäkt på fondandelar"
                                    value={fondandelar}
                                    onChange={(e) => setFondandelar(e.target.value)}
                                    onBlur={() => applyAmount(fondandelar, setFondandelarApplied, 'schablonintäkt på fondandelar')}
                                />
                            </div>
                            {preview?.lan420 && (preview.lan420.derived > 0 || preview.lan420.overridden) && (
                                <div className="skv-question">
                                    <span className="skv-label">
                                        Lån från aktieägare (4.20): härlett ur 2393/2893 är{' '}
                                        {Number(preview.lan420.derived).toLocaleString('sv-SE')} kr
                                        (kontona klampas var för sig — de kvittas inte). Avser en del
                                        av saldot någon som inte är aktieägare (t.ex. närstående)
                                        justeras beloppet här.
                                    </span>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className="skv-inline-input"
                                        placeholder={String(preview.lan420.derived)}
                                        aria-label="Lån från aktieägare, justerat belopp"
                                        value={lanOverride}
                                        onChange={(e) => setLanOverride(e.target.value)}
                                        onBlur={() => applyAmount(lanOverride, setLanOverrideApplied, 'lån från aktieägare')}
                                    />
                                </div>
                            )}
                            {(taxReview?.skatt8920 !== 0 || needs8930 || needs8980) && taxReview && (
                                <>
                                    <div className="skv-section-title">Skattekonton utanför 8910–8919</div>
                                    {taxReview.skatt8920 !== 0 && (
                                        <p className="skv-note">
                                            Konto 8920 (skatt p.g.a. ändrad beskattning):{' '}
                                            {Number(taxReview.skatt8920).toLocaleString('sv-SE')} kr —
                                            svensk inkomstskatt är aldrig avdragsgill och har lagts
                                            till i 4.3 a automatiskt.
                                        </p>
                                    )}
                                    {needs8930 && (
                                        <div className="skv-question">
                                            <span className="skv-label">
                                                Konto 8930 Restituerad skatt har saldo{' '}
                                                {Number(taxReview.restitueradSkatt8930).toLocaleString('sv-SE')} kr.
                                                Avser återbetalningen tidigare <em>ej avdragsgill</em> skatt
                                                (då är den skattefri och dras av på 4.5 c)?
                                            </span>
                                            <span className="skv-seg" role="group" aria-label="Restituerad skatt">
                                                <button
                                                    type="button"
                                                    className={skatt8930 === 'ej_skattepliktig' ? 'active' : ''}
                                                    aria-pressed={skatt8930 === 'ej_skattepliktig'}
                                                    onClick={() => setSkatt8930('ej_skattepliktig')}
                                                >
                                                    Skattefri
                                                </button>
                                                <button
                                                    type="button"
                                                    className={skatt8930 === 'skattepliktig' ? 'active' : ''}
                                                    aria-pressed={skatt8930 === 'skattepliktig'}
                                                    onClick={() => setSkatt8930('skattepliktig')}
                                                >
                                                    Skattepliktig
                                                </button>
                                            </span>
                                        </div>
                                    )}
                                    {needs8980 && (
                                        <div className="skv-question">
                                            <span className="skv-label">
                                                Konto 8980 Övriga skatter har saldo{' '}
                                                {Number(taxReview.ovrigaSkatter8980).toLocaleString('sv-SE')} kr.
                                                Är posten svensk skatt (ej avdragsgill — återläggs på 4.3 c)
                                                eller utländsk skatt (kan vara avdragsgill — ingen justering)?
                                            </span>
                                            <span className="skv-seg" role="group" aria-label="Övriga skatter">
                                                <button
                                                    type="button"
                                                    className={skatt8980 === 'svensk' ? 'active' : ''}
                                                    aria-pressed={skatt8980 === 'svensk'}
                                                    onClick={() => setSkatt8980('svensk')}
                                                >
                                                    Svensk skatt
                                                </button>
                                                <button
                                                    type="button"
                                                    className={skatt8980 === 'utlandsk' ? 'active' : ''}
                                                    aria-pressed={skatt8980 === 'utlandsk'}
                                                    onClick={() => setSkatt8980('utlandsk')}
                                                >
                                                    Utländsk skatt
                                                </button>
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="skv-section-title">Upplysningar</div>
                            <div className="skv-question">
                                <span className="skv-label">
                                    Har en uppdragstagare (t.ex. redovisningskonsult) biträtt vid
                                    upprättandet av årsredovisningen?
                                </span>
                                {renderQuestion(bitratt, setBitratt, 'Biträde vid upprättandet')}
                            </div>
                            <div className="skv-question">
                                <span className="skv-label">
                                    Har årsredovisningen varit föremål för revision?
                                </span>
                                {renderQuestion(revision, setRevision, 'Revision')}
                            </div>
                            {!questionsAnswered && (
                                <p className="skv-note">
                                    Besvara båda frågorna — de skrivs som kryss på blanketten och
                                    krävs innan filerna kan skapas.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Generate */}
                    <div className="ye-ps-section">
                        {generateError && (
                            <div className="alert alert-danger mb-3" style={{ fontSize: '0.875rem' }}>
                                {generateError}
                            </div>
                        )}
                        <div className="ye-proposal-actions" style={{ marginTop: 0 }}>
                            <button
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={handleGenerate}
                                disabled={!canGenerate}
                            >
                                <Download size={16} />
                                {generating ? 'Skapar filer…' : 'Skapa och ladda ner SRU-filer'}
                            </button>
                            {!questionsAnswered && (
                                <span className="ye-footer-hint">
                                    Besvara frågorna i INK2S-blanketten ovan först.
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Upload guidance */}
                    <div className="ye-ps-section">
                        <h5 className="ye-section-heading">
                            <ShieldCheck size={16} style={{ marginRight: 6 }} />
                            Så lämnar du in
                        </h5>
                        <ol className="mb-2" style={{ maxWidth: '68ch' }}>
                            <li>Ladda ner filerna <code>info.sru</code> och <code>blanketter.sru</code> ovan.</li>
                            <li>
                                Logga in i Skatteverkets filöverföringstjänst och ladda upp båda
                                filerna. Inlämningen görs av en firmatecknare eller ett
                                deklarationsombud.
                            </li>
                            <li>
                                Kontrollera kvittensen från Skatteverket — först då är deklarationen
                                inlämnad.
                            </li>
                        </ol>
                        <a
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            href={FILOVERFORING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Öppna Skatteverkets filöverföring
                            <ExternalLink size={14} />
                        </a>
                    </div>
                </>
            )}

            {gateError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{gateError.message}</span>
                </div>
            )}

            {!completed && (
                <FilingSubmissionPanel
                    companyId={companyId}
                    processId={processId}
                    generatedEvidence={generationEvidence}
                    onStatusChange={setFilingConfirmed}
                />
            )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Deklarationen är markerad som inlämnad
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {generated
                                ? 'När du fått Skatteverkets kvittens — markera steget som klart.'
                                : 'Skapa filerna och lämna in dem hos Skatteverket först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy || !filingConfirmed}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default DeklarationStep;
